// hooks/useVisibleDocs.ts
//
// Filters the customer-docs manifest down to articles the current user
// can see, based on:
//   - module/submenu gating (delegated to useMenuVisibility — which has NO
//     superadmin bypass: docs follow the same module visibility as the nav)
//   - account role from activeAccount?.role (SUPPORT bypasses role gating
//     since they're internal staff helping with the customer's account)
//
// This is the SINGLE source of truth for "what docs does this user see".
// Sidebar tree, search index, DocsLink resolution, and DocsRouteGuard
// must all consume the output of this hook — never read manifest directly.

import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { manifest } from "@/docs/manifest";
import { isDocVisible } from "@/docs/gating";
import type { DocMeta } from "@/types/docs";

export function useVisibleDocs(): DocMeta[] {
  const { isMenuVisible, isSubmenuVisible } = useMenuVisibility();
  const { activeAccount, user } = useAuth();
  const role = activeAccount?.role;
  const isSuperadmin = user?.isSuperadmin ?? false;
  const featureFlags = activeAccount?.featureFlags;

  return useMemo(() => {
    // Gating logic lives in the shared `isDocVisible` so docs (here) and
    // the help drawer (useVisibleHelpTopics) can never drift.
    const gate = { isMenuVisible, isSubmenuVisible, role, isSuperadmin, featureFlags };
    return manifest.filter((doc) => isDocVisible(doc, gate));
  }, [isMenuVisible, isSubmenuVisible, role, isSuperadmin, featureFlags]);
}
